import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, ArrowRight, Award, BookOpen } from "lucide-react";

/**
 * PublicCertificate — anonymous /c/:token view of a completion.
 * Same visual as the authed certificate, minus the print/share toolbar
 * and the learner email.  Emits proper meta tags via document.title +
 * a dynamic <meta og:image> that points at the backend PNG generator.
 */
export default function PublicCertificate() {
  const { token } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.get(`/adventures/share/${token}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ notfound: true }); });
    return () => { cancelled = true; };
  }, [token]);

  // Update page title + og:image dynamically for social scrapers that
  // execute JS (LinkedIn/Twitter don't, but this at least helps the
  // browser tab and users who share the raw URL).
  useEffect(() => {
    if (!data || data.notfound) return;
    document.title = `${data.learner_name} finished ${data.title} · Durga Programming Adventures`;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let og = document.querySelector('meta[property="og:image"]');
    if (!og) {
      og = document.createElement("meta");
      og.setAttribute("property", "og:image");
      document.head.appendChild(og);
    }
    og.setAttribute("content", `${backendUrl}/api/adventures/share/${token}/og.png`);
  }, [data, token]);

  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (data.notfound) {
    return (
      <div className="container-narrow py-24 text-center" data-testid="public-cert-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-3">This certificate has wandered off.</h1>
        <p className="text-ink2 mb-4">The link may have expired, or the learner deleted their completion.</p>
        <Link to="/adventures" className="btn-outline-ink text-sm inline-flex items-center gap-1.5">Browse adventures <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  const total = (data.act_snapshots || []).reduce((n, a) => n + (a.chapters?.length || 0), 0);
  const dateStr = new Date(data.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="bg-cream py-8 md:py-14" data-testid={`public-cert-${token}`}>
      <div className="container-narrow">
        <div className="relative rounded-3xl border-4 border-ink bg-cream shadow-[12px_12px_0_0_#1F1A17] overflow-hidden p-8 md:p-14">
          <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-marigold mb-3 inline-flex items-center gap-2">
            <Award className="w-3.5 h-3.5" /> Certificate of Expedition
          </div>
          <p className="text-ink2 italic mb-2">This is to certify that</p>
          <div className="font-heading text-4xl md:text-6xl font-semibold text-ink leading-tight mb-6" data-testid="public-cert-name">
            {data.learner_name}
          </div>
          <p className="text-ink2 mb-1">has walked, at their own pace, the whole of</p>
          <div className="font-heading text-3xl md:text-5xl font-semibold text-marigold italic leading-tight mb-4 gold-underline inline-block">
            {data.title}
          </div>
          <p className="text-ink2 italic max-w-3xl">{data.one_line}</p>

          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-marigold">Completed on</div>
              <div className="font-heading text-lg text-ink">{dateStr}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-marigold">Chapters walked</div>
              <div className="font-heading text-lg text-ink">{total}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-marigold">Adventure</div>
              <div className="font-heading text-lg text-ink">{data.kicker}</div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-ink/25 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="font-hand text-3xl text-ink mb-1">Pradhan</div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/70">
                Ms. Pradhan · teacher-in-residence
              </div>
            </div>
            <Link
              to={`/adventures/${data.slug}`}
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="public-cert-open-adventure"
            >
              <BookOpen className="w-4 h-4" /> Read this adventure <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-ink2/70">
          Durga Programming Adventures · <Link to="/" className="underline hover:text-marigold">start your own</Link>
        </div>
      </div>
    </div>
  );
}
